import React from 'react';
import { Link } from 'react-router-dom';
import Footer from '../../components/Footer';

const s = {
  page: { fontFamily: 'system-ui, -apple-system, sans-serif', background: '#f8fafc' },
  hero: {
    background: '#080d1a',
    padding: '48px 24px 64px',
    textAlign: 'center',
  },
  heroLabel: {
    display: 'inline-block',
    background: 'rgba(99,102,241,0.15)',
    color: '#818cf8',
    fontSize: 13,
    fontWeight: 600,
    padding: '5px 14px',
    borderRadius: 20,
    marginBottom: 20,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
  },
  heroTitle: {
    fontSize: 38,
    fontWeight: 800,
    color: '#f8fafc',
    marginBottom: 16,
    letterSpacing: '-0.8px',
    lineHeight: 1.2,
    maxWidth: 700,
    margin: '0 auto 16px',
  },
  hereMeta: {
    fontSize: 14,
    color: '#64748b',
    marginTop: 16,
  },
  body: {
    maxWidth: 720,
    margin: '0 auto',
    padding: '56px 24px 80px',
  },
  h2: {
    fontSize: 22,
    fontWeight: 700,
    color: '#0f172a',
    marginTop: 40,
    marginBottom: 14,
  },
  p: {
    fontSize: 16,
    color: '#374151',
    lineHeight: 1.75,
    marginBottom: 20,
  },
  ul: {
    paddingLeft: 20,
    marginBottom: 20,
  },
  li: {
    fontSize: 16,
    color: '#374151',
    lineHeight: 1.75,
    marginBottom: 8,
  },
  infoBox: {
    background: '#eff6ff',
    border: '1px solid #bfdbfe',
    borderLeft: '4px solid #2563eb',
    borderRadius: 8,
    padding: '16px 20px',
    marginBottom: 28,
    fontSize: 15,
    color: '#1e40af',
    lineHeight: 1.65,
  },
  cta: {
    background: 'linear-gradient(135deg, #1e3a8a 0%, #1e40af 100%)',
    borderRadius: 14,
    padding: '36px 32px',
    marginTop: 52,
    textAlign: 'center',
  },
  ctaTitle: { fontSize: 22, fontWeight: 700, color: '#fff', marginBottom: 10 },
  ctaText: { fontSize: 15, color: 'rgba(255,255,255,0.8)', marginBottom: 24, lineHeight: 1.6 },
  ctaBtn: {
    display: 'inline-block',
    background: '#fff', color: '#1e40af', fontWeight: 700,
    fontSize: 15, padding: '12px 28px', borderRadius: 8, textDecoration: 'none',
  },
  backLink: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 14,
    color: '#64748b',
    textDecoration: 'none',
    marginBottom: 32,
  },
};

export default function HowToFindEUTenders() {
  return (
    <div style={s.page}>

      <div style={s.hero}>
        <div style={s.heroLabel}>Guide</div>
        <h1 style={s.heroTitle}>How to find EU public tenders for your business</h1>
        <div style={s.hereMeta}>March 2026 &nbsp;·&nbsp; 8 min read</div>
      </div>

      <div style={s.body}>
        <Link to="/blog" style={s.backLink}>← Back to blog</Link>

        <p style={s.p}>
          Every year, EU governments and public bodies spend over €2 trillion on goods, services, and works — roughly 14% of the EU's entire GDP. Yet the vast majority of small and medium-sized businesses never bid on a single contract. The most common reason? They simply don't know the tenders exist, or they assume the process is too complex to navigate.
        </p>
        <p style={s.p}>
          The good news: finding relevant EU public tenders is more systematic than most businesses realise. This guide walks you through exactly where to look, what to look for, and how to stop missing contracts your company could win.
        </p>

        <h2 style={s.h2}>Start with TED — the EU's official tender database</h2>
        <p style={s.p}>
          <strong>TED</strong> (Tenders Electronic Daily) is the online version of the EU's Official Journal dedicated to public procurement. It is published five times a week and contains notices from across all 27 EU member states, plus notices from the EEA and beyond.
        </p>
        <p style={s.p}>
          TED publishes several types of notice: contract notices (new opportunities), contract award notices (showing what was awarded and to whom), and prior information notices (advance warning of upcoming contracts). For most businesses, contract notices are the starting point.
        </p>
        <div style={s.infoBox}>
          TED publishes around 750,000 contract notices per year. Without filtering, it is essentially unsearchable. The key is learning to filter by CPV code, country, and contract value — or using a tool that does this for you.
        </div>

        <h2 style={s.h2}>Understand CPV codes</h2>
        <p style={s.p}>
          Every public tender in the EU is classified using <strong>CPV codes</strong> (Common Procurement Vocabulary) — an 8-digit number that describes the type of contract. For example, 72000000 covers IT services, while 79410000 covers business and management consultancy.
        </p>
        <p style={s.p}>
          When searching for tenders, identifying your relevant CPV codes is the single most effective filter you can apply. Most procurement portals — including TED — allow you to search by CPV code directly. You can browse the full hierarchy at the EU's official CPV portal.
        </p>
        <p style={s.p}>
          Many companies miss tenders because they only search their primary CPV code and miss related ones. A translation agency might search for 79530000 (translation services) but miss 79540000 (interpretation services) or 79000000 (business services, broader). Build a list of all CPV codes that could reasonably apply to your work.
        </p>

        <h2 style={s.h2}>Use national procurement portals too</h2>
        <p style={s.p}>
          TED only captures contracts above EU threshold values (currently €140,000 for central government services, €215,000 for other public bodies). Contracts below these thresholds are typically published on <strong>national procurement portals</strong>.
        </p>
        <ul style={s.ul}>
          <li style={s.li}><strong>Netherlands:</strong> TenderNed</li>
          <li style={s.li}><strong>Germany:</strong> DTVP, DTAD, vergabe.de</li>
          <li style={s.li}><strong>Belgium:</strong> e-Procurement (publicprocurement.be)</li>
          <li style={s.li}><strong>France:</strong> BOAMP, marches-publics.info</li>
          <li style={s.li}><strong>Austria:</strong> Bundesvergabeamt (BVA), Austrian Federal Procurement Agency</li>
        </ul>
        <p style={s.p}>
          If you operate in a specific country, subscribing to that country's national portal is essential — you will see far more relevant lower-value contracts than TED alone will show you.
        </p>

        <h2 style={s.h2}>Set up alerts — don't search manually every day</h2>
        <p style={s.p}>
          Manually checking procurement portals every day is not a sustainable strategy. Instead, set up saved searches with email alerts. TED supports this natively: create a search with your CPV codes and countries, save it, and receive daily digests.
        </p>
        <p style={s.p}>
          The challenge with alerts from raw portals is noise. You will receive many irrelevant notices alongside relevant ones. The best way to filter is to use a relevance scoring layer on top — either manually (score each notice against your capabilities) or using an AI tool that does this automatically.
        </p>

        <h2 style={s.h2}>Assess quickly: is this worth pursuing?</h2>
        <p style={s.p}>
          Even after filtering, you need a fast way to decide whether a specific tender is worth the effort of a full bid. Ask yourself:
        </p>
        <ul style={s.ul}>
          <li style={s.li}>Do we have at least 70% of the required qualifications and experience?</li>
          <li style={s.li}>Is the contract value worth the time investment in writing the bid?</li>
          <li style={s.li}>Are the deadline and submission requirements realistic for our team?</li>
          <li style={s.li}>Have we done similar work before that we can evidence?</li>
          <li style={s.li}>Do we have any prior relationship with this contracting authority?</li>
        </ul>
        <p style={s.p}>
          If you answer no to three or more of these, the tender is probably not worth pursuing. Selective bidding — focusing effort on the tenders you are most likely to win — consistently outperforms blanket bidding on everything that looks vaguely relevant.
        </p>

        <h2 style={s.h2}>Build a pipeline, not a one-off search</h2>
        <p style={s.p}>
          The businesses that win public contracts consistently treat tender discovery as a pipeline rather than a one-time event. This means:
        </p>
        <ul style={s.ul}>
          <li style={s.li}>Reviewing new notices at least weekly</li>
          <li style={s.li}>Tracking upcoming renewals of contracts you know exist (check award notices from 1–3 years ago)</li>
          <li style={s.li}>Monitoring prior information notices (PINs) that signal upcoming large contracts</li>
          <li style={s.li}>Building relationships with contracting authorities before opportunities are advertised</li>
        </ul>
        <p style={s.p}>
          Procurement databases are also a goldmine of competitive intelligence. Award notices tell you who won, at what price, and with what contract structure — invaluable data for shaping your future bids.
        </p>

        <div style={s.cta}>
          <div style={s.ctaTitle}>Let Jyto do the searching for you</div>
          <div style={s.ctaText}>
            Jyto monitors EU procurement databases daily and uses AI to surface the contracts most relevant to your company's profile — so you get a ranked shortlist, not a firehose of notices.
          </div>
          <Link to="/signup" style={s.ctaBtn}>Try Jyto free</Link>
        </div>
      </div>

      <Footer />
    </div>
  );
}
